import { FlowCXController } from '~/components/ClientExperience/OneFlow/controllers/flow-cx.controller';
import type { PublicCXRootStore } from '~/components/ClientExperience/stores/cx-root.store';
import { trackError } from '~/services/logger/error.utils';
import { getLeadFormSessionStorageMethods } from '~/components/CXLeadForm/leadFormSessionStorage.utils';
import { ErrorType } from '~/components/ClientExperience/OneFlow/stores/flow-view.ui.store';
import { TLeadFormSubmitSchedulerData } from '~/components/ClientExperience/OneFlow/components/FlowView/LeadFormSubmit/LeadFormSubmit.types';
import { LEAD_FORM_RECAPTCHA_ACTION } from '~/constants/lead-forms.consts';
import useAnalytics from '~/common/useAnalytics';
import { IPayActionResponse, IPayProps } from '~/types/pay.types';
import { getForterToken } from '~/utils/client-experience.utils';
import { FORM_FIELDS_TO_SKIP_VALIDATION } from '~/components/ClientExperience/OneFlow/components/FlowView/FlowForm.utils';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { getFixedT } from 'i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { isEmpty } from 'lodash';
import {
    ILeadFormCxExperienceResponse,
    IPublicFlowSubmitResponse
} from '~/types/server.types';
import { logInfo } from '~/services/logger/logger.utils';
import dayjs from '~/common/dayjs';

interface IHandleFlowErrorProps {
    error: any;
    callback?: Function;
    pushed?: boolean;
}

export class LeadFormCxController extends FlowCXController {
    rootStore: PublicCXRootStore;

    constructor(rootStore: PublicCXRootStore) {
        super(rootStore);
        this.rootStore = rootStore;
    }

    async fetchFlow(flowId: string, update = true) {
        const { flowService, companyStore } = this.rootStore;

        try {
            const response = await flowService.fetchFlow(
                flowId,
                companyStore.currCompany?.default_timezone
            );

            if (update) {
                this.updateFlow(
                    response.flow,
                    response.pages,
                    response.blocks,
                    response.variables
                );
            }

            return response;
        } catch (error) {
            this.handleFlowError({ error: ErrorType.flowNotFound });
        }
    }

    async getLeadFormNavExperience(): Promise<ILeadFormCxExperienceResponse> {
        const { flowService } = this.rootStore;

        try {
            return await flowService.getLeadFormNavExperience();
        } catch (error) {
            trackError({ error });
            return { new_nav_enabled: false };
        }
    }

    async reloadFlow() {
        const {
            flowViewUiStore: { selectedFlowId, setError }
        } = this.rootStore;

        setError(null);
        await this.fetchFlow(selectedFlowId, true);
    }

    getNameAndEmail() {
        const {
            flowViewUiStore: {
                selectedFlowId,
                selectedFlow: { leadDetailsBlock }
            }
        } = this.rootStore;

        const { getValues } = getLeadFormSessionStorageMethods({
            flowId: selectedFlowId
        });
        const data = getValues();
        const { leadInfoEmailVarId, leadInfoFullNameVarId } =
            leadDetailsBlock.leadDetailsVariableIds;
        const leadInfoEmail = data[leadInfoEmailVarId];
        const leadInfoFullName = data[leadInfoFullNameVarId];

        return { leadInfoEmail, leadInfoFullName };
    }

    async submitLeadForm({
        executeHBRecaptchaV3,
        track,
        gratuity,
        stripePaymentMethod
    }: IPayProps): Promise<IPayActionResponse | any> {
        const {
            flowViewUiStore: {
                history,
                selectedFlow,
                selectedFlowId,
                isEmbeddedWidget,
                selectedFlow: {
                    scheduleContainerBlock,
                    revision_id: revisionId,
                    invoiceContainerBlock,
                    hasInvoiceBlock,
                    areAllRequiredBlocksFilled
                },
                setIsFormSubmitting,
                reset
            },
            flowStore,
            leadFormService,
            scheduleCtrl,
            leadFormPendingTaskCtrl,
            flowSessionStore,
            toastStore
        } = this.rootStore;

        track('click send in submit dialog', { action: 'click' });

        const t = getFixedT(
            null,
            NAMESPACES.CLIENT_EXPERIENCE,
            'new_cx_navigation.member_toasts'
        );

        if (!areAllRequiredBlocksFilled) {
            toastStore.add({
                text: t('general.text'),
                type: ToastType.Error
            });
            return;
        }

        const startPufSubmitTime = Date.now();

        const flowSlugOrId = isEmbeddedWidget
            ? selectedFlowId
            : selectedFlow.configuration.flow_link_slug || selectedFlowId;

        const { setVariable, deleteSessionStorage, getValues, deleteVariable } =
            getLeadFormSessionStorageMethods({
                flowId: selectedFlowId
            });

        FORM_FIELDS_TO_SKIP_VALIDATION.forEach(variableToSkip =>
            deleteVariable({ variableId: variableToSkip })
        );
        // we remove these fields since they are created only for validations
        // on clicking next in the cx flow view.
        // they shouldn't be submitted since submitting creates an instance with
        // all the variables copied from the public file to the instance.

        try {
            setIsFormSubmitting(true);

            // data contains all the variables from the session storage.
            // we transform what we need for the api call- lead info, slot details , pay data,
            // and still pass data since all those variables contain more info that will be saved to the
            // instance created from the public file.
            const data = getValues();
            const { leadInfoEmail, leadInfoFullName } = this.getNameAndEmail();

            // -------- handle selected scheduler slot ------------ //
            let scheduleData: TLeadFormSubmitSchedulerData;
            let sessionName: string = '';

            const leadPhoneNumber = selectedFlow.hasLinkedPhoneQuestion
                ? data[selectedFlow.linkedPhoneQuestion.connected_variable_id]
                : null;

            const slotSelected = scheduleContainerBlock?.isSlotSelected;
            if (slotSelected) {
                const { memberIds, error } = await scheduleCtrl.validateSlot(
                    scheduleContainerBlock,
                    scheduleContainerBlock.selectedDate,
                    scheduleContainerBlock.clientTimezone
                );

                if (error) {
                    this.handleFlowError({ error });
                    return false;
                }

                sessionName = scheduleContainerBlock.sessionName;

                scheduleData = {
                    page_id: scheduleContainerBlock.page_id,
                    data: {
                        selected_date: scheduleContainerBlock.selectedDate,
                        client_timezone: scheduleContainerBlock.clientTimezone,
                        user_ids: memberIds
                    }
                };
            }

            const recaptchResponse = await executeHBRecaptchaV3(
                LEAD_FORM_RECAPTCHA_ACTION
            );
            const recaptchaData = {
                'g-recaptcha-response-v3': recaptchResponse
            };

            const smsConsentShown = flowSessionStore.smsConsentShown;
            const sms_consent_text_version = smsConsentShown ? 1 : null;
            const time_zone_offset = new Date().getTimezoneOffset();

            const formattedData = this.formatValuesBeforeSubmit(data);

            const response = await leadFormService.submitLeadForm({
                data: formattedData,
                recaptchaData,
                email: leadInfoEmail,
                flowId: selectedFlowId,
                fullName: leadInfoFullName,
                revisionId,
                scheduleData,
                gratuity,
                stripePaymentMethod,
                forterToken: getForterToken(),
                hasInvoiceBlock,
                sms_consent_text_version,
                leadPhoneNumber,
                time_zone_offset
            });

            const pullingUrl = response.url;
            const taskId = response.task_id;

            let fetchResponse;
            let currentLogTime;
            const startSubmitTimeAfterTaskId = Date.now();
            try {
                currentLogTime = this.getLogTime();

                logInfo(
                    `Puf submit UUID ${taskId} before register to pending task start`,
                    {
                        currentLogTime,
                        currentTimeTest: this.getLogTime(),
                        event: 'Puf submit to Unicorn. before register to pending task',
                        url: pullingUrl,
                        uuid: taskId,
                        startPufSubmitTime,
                        startSubmitTimeAfterTaskId
                    }
                );
                fetchResponse =
                    (await leadFormPendingTaskCtrl.createPublicPendingTask(
                        {
                            url: pullingUrl,
                            taskId: taskId
                        },
                        async token => {
                            return leadFormService.fetchSubmitResponse(token);
                        }
                    )) as IPublicFlowSubmitResponse;
            } catch (error) {
                this.handleFlowError({ error, pushed: true });
                return false;
            }

            const startPufEndTime = Date.now();
            const totalPufSubmitTime =
                (startPufEndTime - startPufSubmitTime) / 1000;
            const totalSubmitTimeAfterGettingTaskId =
                (startPufEndTime - startSubmitTimeAfterTaskId) / 1000;

            logInfo(
                `startPufEndTime: ${startPufEndTime}, startPufEndTime: ${startPufEndTime}`,
                {
                    startPufSubmitTime,
                    startPufEndTime,
                    totalPufSubmitTime,
                    startSubmitTimeAfterTaskId,
                    totalSubmitTimeAfterGettingTaskId,
                    event: `Puf start and end time`,
                    uuid: taskId
                }
            );

            currentLogTime = this.getLogTime();

            logInfo(
                `Puf submit UUID ${taskId}, CPT end. Duration: ${totalPufSubmitTime} s. Includes: payment ${hasInvoiceBlock}, schedule: ${!!scheduleData}`,
                {
                    currentLogTime,
                    fetchResponse,
                    event: `Puf submit after pending task end`,
                    uuid: taskId,
                    payment_submit: hasInvoiceBlock,
                    schedule_submit: !!scheduleData,
                    totalPufSubmitTime,
                    totalSubmitTimeAfterGettingTaskId
                }
            );
            // Steps after successful submit
            deleteSessionStorage();

            // we need to have a variable that tells us there has been a successful submit,
            // so we can redirect from the thank you page back to the first page.
            setVariable({ variableId: 'is_submit_successful', value: true });

            const scheduleVariable = fetchResponse.variables?.find(
                variable => variable.type === 'schedule_container_variable'
            );

            if (!!scheduleContainerBlock && slotSelected) {
                setVariable({
                    variableId: 'schedule_session_data',
                    value: !isEmpty(scheduleVariable?.value)
                        ? scheduleVariable?.value
                        : {}
                });
            }

            const paymentVariables = fetchResponse.variables?.filter(
                variable => variable.type === 'payment_variable'
            );

            if (!!invoiceContainerBlock) {
                setVariable({
                    variableId: 'payment_data',
                    value: paymentVariables.length
                        ? {
                              ...paymentVariables[0]?.value,
                              numberOfPayments: paymentVariables.length
                          }
                        : {}
                });
            }

            setVariable({
                variableId: 'redirectUrl',
                value: fetchResponse?.redirect_url || null
            });
            setVariable({
                variableId: 'digital_products_data',
                value: fetchResponse?.digital_products || []
            });
            setVariable({
                variableId: 'import_template_link',
                value: fetchResponse?.import_template_link || null
            });

            // this is for when the payment was processed but the session was not schedules.
            // we keep the session name as a variable to display in the thank you page.
            // if the variable exists - means there has been an error scheduling the session.
            if (
                slotSelected &&
                paymentVariables.length &&
                !scheduleVariable?.value?.calendar_item_id
            ) {
                setVariable({ variableId: 'session_name', value: sessionName });
            }

            track('public submit success redirect to thank you page', {
                action: 'action'
            });
            logInfo('lead form submit successful');

            // we want to show the personalized slug in the flow-submitted url if it exists,
            // yet we still need the flowId for data and storage purposes

            const domain = isEmbeddedWidget ? 'embed' : 'public';
            history.push(`/${domain}/${flowSlugOrId}/flow-submitted`, {
                flowId: selectedFlowId
            });

            reset();
            flowStore.reset();

            return true;
        } catch (error) {
            this.handleFlowError({ error });
            return false;
        } finally {
            setIsFormSubmitting(false);
        }
    }

    private getLogTime() {
        return dayjs().format('MMM DD YYYY HH:mm:ss');
    }

    handleFlowError({ error, callback, pushed }: IHandleFlowErrorProps) {
        const { flowViewUiStore, toastStore } = this.rootStore;
        trackError({ error });
        let errorType = undefined;
        if (typeof error === 'object') {
            errorType =
                error?.response?.data?.error_type || error?.data?.error_type;
        }
        if (typeof error === 'string') {
            errorType = error;
        }
        if (pushed) {
            const t = getFixedT(
                null,
                NAMESPACES.CLIENT_EXPERIENCE,
                'lead_form_submit_error'
            );
            toastStore.add({
                type: ToastType.Error,
                title: t('title'),
                text: error,
                autoDismissMs: 0
            });
        }
        if (errorType === ErrorType.cardDeclined) {
            const t = getFixedT(
                null,
                NAMESPACES.CLIENT_EXPERIENCE,
                'public_payment_error'
            );
            toastStore.add({
                type: ToastType.Error,
                title: t('title'),
                text: t('description'),
                autoDismissMs: 0
            });
        } else {
            flowViewUiStore.setError(errorType || ErrorType.error);
        }

        if (!callback) {
            return;
        }

        callback(error);
    }

    goToPage(pageId: string) {
        const { history, selectedFlowId, findPageIndex, selectPage } =
            this.rootStore.flowViewUiStore;

        const pageIndex = findPageIndex(pageId);
        const pageSchema = this.createPageSchema(pageIndex);
        selectPage(pageId);
        history.push(`/public/${selectedFlowId}/${pageSchema}`);
    }

    async onFormSubmit({
        executeHBRecaptchaV3,
        track,
        values
    }: {
        executeHBRecaptchaV3?: (action: string) => Promise<string>;
        track?: ReturnType<typeof useAnalytics>['track'];
        values: Record<string, any>;
    }) {
        // each page submit saves variables to session storage
        const {
            flowViewUiStore: {
                selectedFlowId,
                selectedPage: { isLastPage }
            }
        } = this.rootStore;

        const { setVariable } = getLeadFormSessionStorageMethods({
            flowId: selectedFlowId
        });
        const variables = Object.entries(values);
        variables.forEach(([variableId, value]) =>
            setVariable({ variableId, value })
        );

        if (isLastPage) {
            await this.submitLeadForm({
                executeHBRecaptchaV3,
                track
            });
        }
    }
}
